import { toast } from "@amtana/design-system";
import { currentLanguage, phrase, readWords, translate } from "@amtana/i18n";
import type { PlainKey, Words } from "@amtana/i18n";
import { TRPCClientError } from "@trpc/client";

import { BROWSER_CATALOGS } from "./browser-copy.ts";
import type { BrowserCatalog } from "./browser-copy.ts";

// What a user reads when something fails (packages/module ADR-001). Every
// failure arrives as a code; each browser module writes copy for its own
// contract's codes (`withErrorCopy`), the framework writes copy for its own,
// and `explainError` resolves any failure to a title, a description and, when
// the failure is ours, a reference the logs know. Nothing here renders a
// server message or a code.

export type ErrorCopy = { readonly title: Words; readonly description?: Words };

/** Copy for every code a contract declares: `satisfies ErrorCopyFor<typeof X_ERROR_CODES>`. */
export type ErrorCopyFor<TCodes extends Readonly<Record<string, string>>> = {
  readonly [Code in TCodes[keyof TCodes]]: ErrorCopy;
};

export type ExplainedError = {
  readonly title: string;
  readonly description: string;
  /** Present when the failure is ours: the request id, for the user to quote and the logs to find. */
  readonly reference: string | null;
};

/** The framework's own codes: the wire's (ADR-001, point 2) and the browser's ("offline"). */
type FrameworkKey = PlainKey<BrowserCatalog>;

const framework = (title: FrameworkKey, description: FrameworkKey): ErrorCopy => ({
  title: phrase(BROWSER_CATALOGS, title),
  description: phrase(BROWSER_CATALOGS, description),
});

const FRAMEWORK_COPY: Readonly<Record<string, ErrorCopy>> = {
  unknown: framework("unknownTitle", "unknownDescription"),
  validation_error: framework("validationTitle", "validationDescription"),
  not_signed_in: framework("signedOutTitle", "signedOutDescription"),
  forbidden: framework("forbiddenTitle", "forbiddenDescription"),
  // S9.01, ADR-005 point 3: a staff door answers not_found for anyone who is not staff, so a
  // screen behind one (such as /ops) that throws this to its boundary reads exactly like a
  // page that does not exist, never a hint that it does.
  not_found: framework("notFoundTitle", "notFoundDescription"),
  offline: framework("offlineTitle", "offlineDescription"),
  // ADR-010 point 11: the host's own refusals, as framework codes every screen may meet.
  rate_limited: framework("rateLimitedTitle", "rateLimitedDescription"),
  payload_too_large: framework("payloadTooLargeTitle", "payloadTooLargeDescription"),
};

let registry: Readonly<Record<string, ErrorCopy>> = FRAMEWORK_COPY;

/** Installs every module's copy; createUi calls it once with what install() merged. */
export function setErrorCopy(modules: Readonly<Record<string, ErrorCopy>>): void {
  registry = { ...FRAMEWORK_COPY, ...modules };
}

type WireData = {
  readonly appCode?: unknown;
  readonly requestId?: unknown;
  readonly appDetails?: unknown;
};

function wireOf(error: unknown): { code: string; requestId: string | null; details: unknown } {
  if (!(error instanceof TRPCClientError)) return { code: "unknown", requestId: null, details: undefined };
  const data = error.data as WireData | undefined;
  // No data: the answer was not ours (a network failure, a proxy's 502), so the server was not reached.
  if (!data || typeof data.appCode !== "string")
    return { code: "offline", requestId: null, details: undefined };
  return {
    code: data.appCode,
    requestId: typeof data.requestId === "string" ? data.requestId : null,
    details: data.appDetails,
  };
}

/** The stable code a refusal carries; branch on it, never render it. */
export function errorCodeOf(error: unknown): string {
  return wireOf(error).code;
}

/** The id the server gave an unexpected error, for the user to quote and the logs to find (process ADR-002). */
export function requestIdOf(error: unknown): string | null {
  return wireOf(error).requestId;
}

/** Whatever opt-in `details` the throwing error attached beside its code (review group 3
 * security finding 7, revising D863's blanket `meta` reach): present only for the specific
 * errors that declared a zod schema for their own shape in their owning contract
 * (`ErasureBlockedError`, so far the only one), never on a masked or 5xx answer. Unknown until
 * parsed through that schema's own `safeParse`; never read or rendered as-is. */
export function errorDetailsOf(error: unknown): unknown {
  return wireOf(error).details;
}

/**
 * Any failure in the user's words. A code with copy reads as its copy; a code
 * nobody wrote copy for reads as the action that failed (`fallbackTitle`),
 * never as the code. An unexpected failure carries its reference.
 */
export function explainError(error: unknown, fallbackTitle: string): ExplainedError {
  const { code, requestId } = wireOf(error);
  const language = currentLanguage();
  const copy = registry[code];
  if (code === "unknown" || !copy) {
    const unknown = FRAMEWORK_COPY["unknown"]?.description;
    return {
      title: fallbackTitle,
      description: unknown ? readWords(unknown, language) : "",
      reference: requestId,
    };
  }
  return {
    title: readWords(copy.title, language),
    description: copy.description ? readWords(copy.description, language) : "",
    reference: requestId,
  };
}

/** The reference as the user reads it: short enough to quote, long enough to find. */
export function referenceText(reference: string): string {
  return translate(BROWSER_CATALOGS, currentLanguage(), {
    key: "reference",
    fill: { reference: reference.slice(0, 12) },
  });
}

/** A write failed: tell the user, in the toast every write uses. `fallbackTitle` names the action. */
export function reportError(error: unknown, fallbackTitle: string): void {
  const explained = explainError(error, fallbackTitle);
  const description = [explained.description, explained.reference ? referenceText(explained.reference) : ""]
    .filter(Boolean)
    .join(" ");
  toast({ tone: "danger", title: explained.title, ...(description ? { description } : {}) });
}
